/**
 * Coalesce text animation — characters materialize from random glyphs
 * to the real text with a left-to-right stagger.
 */

/** Scramble glyph palette used during the cycling phase. */
const SCRAMBLE_GLYPHS = "~!@#$%^&*()+=[]{}|<>?/\\";

export interface CoalesceOptions {
  /** Milliseconds between each character starting to resolve. Default: 6 */
  staggerMs?: number;
  /** Number of random glyph cycles before settling. Default: 2 */
  cyclesPerChar?: number;
  /** Milliseconds per glyph cycle tick. Default: 15 */
  cycleMs?: number;
  /** Called when all characters have resolved. */
  onDone?: () => void;
}

/**
 * Animate text by cycling each character through random glyphs before
 * resolving to the target character, left-to-right with a stagger delay.
 *
 * @param target - The final text to display.
 * @param onFrame - Called each time the display text updates.
 * @param opts - Animation timing options.
 * @returns A cancel function that stops all pending timers.
 */
export function coalesceText(
  target: string,
  onFrame: (text: string) => void,
  opts?: CoalesceOptions,
): () => void {
  const staggerMs = opts?.staggerMs ?? 6;
  const cyclesPerChar = opts?.cyclesPerChar ?? 2;
  const cycleMs = opts?.cycleMs ?? 15;
  const onDone = opts?.onDone;

  // Track all active timer IDs so cancel() can clear them.
  const timers: ReturnType<typeof setTimeout>[] = [];
  let cancelled = false;

  // Handle empty string — complete immediately.
  if (target.length === 0) {
    onFrame("");
    onDone?.();
    return () => {};
  }

  // Current display state: each character starts as a random glyph
  // (spaces stay as spaces).
  const chars: string[] = [];
  for (let i = 0; i < target.length; i++) {
    if (target[i] === " ") {
      chars[i] = " ";
    } else {
      chars[i] = randomGlyph();
    }
  }

  let resolvedCount = 0;
  const totalToResolve = [...target].filter((ch) => ch !== " ").length;

  // Emit the initial scrambled frame.
  onFrame(chars.join(""));

  // If all characters are spaces, we're done immediately.
  if (totalToResolve === 0) {
    onDone?.();
    return () => {};
  }

  // For each non-space character, schedule a staggered cycling sequence.
  let staggerIndex = 0;
  for (let i = 0; i < target.length; i++) {
    if (target[i] === " ") continue;

    const charIndex = i;
    const delay = staggerIndex * staggerMs;
    staggerIndex++;

    // Schedule the cycling for this character.
    const staggerTimer = setTimeout(() => {
      if (cancelled) return;

      let cycle = 0;

      function doCycle(): void {
        if (cancelled) return;

        if (cycle < cyclesPerChar) {
          // Show a random glyph for this character.
          chars[charIndex] = randomGlyph();
          onFrame(chars.join(""));
          cycle++;

          const cycleTimer = setTimeout(doCycle, cycleMs);
          timers.push(cycleTimer);
        } else {
          // Resolve to the real character.
          chars[charIndex] = target[charIndex];
          resolvedCount++;
          onFrame(chars.join(""));

          if (resolvedCount === totalToResolve) {
            onDone?.();
          }
        }
      }

      doCycle();
    }, delay);

    timers.push(staggerTimer);
  }

  // Return cancel function.
  return () => {
    cancelled = true;
    for (const id of timers) {
      clearTimeout(id);
    }
    timers.length = 0;
  };
}

/** Pick a random glyph from the scramble palette. */
function randomGlyph(): string {
  return SCRAMBLE_GLYPHS[Math.floor(Math.random() * SCRAMBLE_GLYPHS.length)];
}
